// @ts-check

import { padStart } from 'lodash-es'
import axios from 'axios'
import type Merch from '../merch'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import { appConstants, merchConstants } from '@/models/constants'
import type CatalogDetails from '@/models/catalogDetails'
import utils, { validateDate } from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import onTemplate from '@/assets/templateLayoutImages/onTemplate.png'

class OnTemplate implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.onTemplate
  name = 'On Template'
  layoutImage = onTemplate
  imgSize: number = 40
  imgScaleFactor: number = 0.08
  titleHeight: number = 0
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  slideWidth: number = 1280
  slideHeight: number = 720
  slideSize = merchConstants.slideSize.wideScreen
  onLogoData: string = ''
  logoWidth = 77
  logoHeight = 77
  logoScaling = 0.5
  articleDetailsHeight: number = 0
  articleAttributes?: string[]
  groupByArticleAttributes?: string[]
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'On Diatype'
  leftPadding = 20
  rightPadding = 20
  monthOrder = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  headerProps = {
    height: 220,
    bottomPadding: 10,
  }

  columnProps = {
    labelWidth: 40,
    labelFontSize: 10,
    labelHeight: 20,
    textAlign: 'center',
    fontStyle: 'normal',
    fontWeight: 'normal',
  }

  rowProps = {
    labelFontSize: 10,
    labelHeight: 20,
    textAlign: 'center',
    fontStyle: 'normal',
    fontWeight: 'normal',

  }

  articleAttributeProp = {
    fontWeight: 'normal',
    fontSize: 4,
    textAlign: 'left',
    fontStyle: 'normal',
  }

  merchLabelAttributes: IMyAttribute[] = []
  mapOfSelectedArticleAttributesSystemNameToDisplayLabel = new Map()
  isMerch: boolean = true

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IMerchTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }
    const options: IMerchTemplateOption[] = []

    // hide restricted attributes
    const ControlAttributeVisibilityToDisplayInOutput = catalog?.Config.ControlAttributeVisibilityToDisplayInOutput
    let allowedAttributes = attributes
    if (ControlAttributeVisibilityToDisplayInOutput && ControlAttributeVisibilityToDisplayInOutput.length) {
      allowedAttributes = attributes.filter((attr) => {
        return !ControlAttributeVisibilityToDisplayInOutput.includes(attr.SystemName)
      })
    }
    options.push({
      name: 'displayAttributes',
      label: 'generateDialog.steps.options.attributesToDisplay',
      type: 'attributeChooser',
      default: ['ModelName', 'StyleUpdate_ON', 'SalesColorName_ON', 'DropDate'],
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 4,
      data: allowedAttributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []
    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code', 'colorwaynumber', 'colorway number'],
    })
    return mapping
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>): Promise<(void | number)> {
    const [onLogo]: any = await this.getHeaderLogosData([
      appConstants.logoPaths.onLogo,
    ])

    this.onLogoData = onLogo
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, merch)
  }

  async getHeaderLogosData(urls: string[]) {
    const loadImage = (url: string) => {
      return new Promise<string>((resolve) => {
        axios.get(url, { responseType: 'arraybuffer' }).then((res) => {
          const base64 = res.data ? `data:image/png;base64,${utils.arrayBufferToBase64(res.data)}` : null
          if (base64) {
            const settings = {
              width: this.logoWidth,
              height: this.logoHeight,
              scaleX: this.logoScaling,
              scaleY: this.logoScaling,
            }
            const image = new Image()
            image.onload = () => {
              resolve(utils.resizeImageAndGetBase64(image, settings) as string)
            }
            image.onerror = () => resolve('')
            image.src = base64
          }
          else {
            resolve('')
          }
        }).catch((err) => {
          console.log(err)
          resolve('')
        })
      })
    }

    const imagePromises = urls.map(loadImage)
    return Promise.all(imagePromises)
  }

  async buildTemplate(catalog: CatalogDetails, data: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, merch: Merch | undefined) {
    const promises: Promise<void | number>[] = []

    const groups = this.generateGroups(catalog, data, myAttributes)
    let ttlSlides = 0
    const currentSlideNbr = 1
    const currentObjects: any[] = []
    let slideNameValue = ''
    const headerGroups = Object.keys(groups)
    headerGroups.sort()
    for (let i = 0; i < headerGroups.length; i++) {
      const accountChannel = headerGroups[i]
      slideNameValue = accountChannel
      // generate header
      await this.onNewPage(catalog, currentObjects, accountChannel)
      let currentTop = this.headerProps.height + this.headerProps.bottomPadding
      let launchMonths = Object.keys(groups[accountChannel])
      launchMonths.sort((a, b) => {
        return this.monthOrder.indexOf(a) - this.monthOrder.indexOf(b)
      })
      launchMonths = launchMonths.splice(0, 6)
      const eachColumnWidth = (this.slideWidth - this.leftPadding - this.rightPadding - this.columnProps.labelWidth) / (launchMonths.length > 6 ? 6 : launchMonths.length < 1 ? 1 : launchMonths.length)
      this.addLaunchMonthsObjects(currentObjects, launchMonths, eachColumnWidth, currentTop)
      currentTop += this.columnProps.labelHeight
      const uniqueVerticalsForAllLaunchMonths: string[] = []
      launchMonths.forEach((month) => {
        const verticals = Object.keys(groups[accountChannel][month])
        verticals.forEach((value) => {
          if (!uniqueVerticalsForAllLaunchMonths.includes(value)) {
            uniqueVerticalsForAllLaunchMonths.push(value)
          }
        })
      })
      uniqueVerticalsForAllLaunchMonths.sort()
      let createNewPage = false
      let currentVerticals = uniqueVerticalsForAllLaunchMonths.length < 4 ? uniqueVerticalsForAllLaunchMonths.splice(0, uniqueVerticalsForAllLaunchMonths.length) : uniqueVerticalsForAllLaunchMonths.splice(0, 4)
      while (currentVerticals.length !== 0) {
        if (createNewPage) {
          createNewPage = false
          // create new page
          if (currentObjects.length > 0) {
            ttlSlides++
            if (this.isMerch) {
              promises.push(this.createSlide(merch, `${slideNameValue} - ${padStart(currentSlideNbr.toString(), 3, '0')}`, currentObjects.slice(), options, this.imgScaleFactor)
                .catch((error) => {
                  // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                  console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                }))
            }
          }
          this.onNewPage(catalog, currentObjects, accountChannel)
          currentTop = this.headerProps.height // + this.headerProps.bottomPadding
          this.addLaunchMonthsObjects(currentObjects, launchMonths, eachColumnWidth, currentTop)
          currentTop += this.columnProps.labelHeight
        }
        const eachRowHeight = (this.slideHeight - this.headerProps.height - this.headerProps.bottomPadding - this.columnProps.labelHeight) / currentVerticals.length
        this.addVerticalLabels(currentObjects, currentVerticals, eachRowHeight, currentTop)
        let leftForEachMonth = this.leftPadding + this.columnProps.labelWidth
        launchMonths.forEach((month) => {
          let top = currentTop
          let maximumArticlesInQuadrant = 0
          currentVerticals.forEach((vertical) => {
            if (groups[accountChannel][month][vertical]) {
              // articles are present
              const articles = groups[accountChannel][month][vertical]
              if (maximumArticlesInQuadrant < articles.length) {
                maximumArticlesInQuadrant = articles.length
              }
            }
          })
          currentVerticals.forEach((vertical) => {
            if (groups[accountChannel][month][vertical]) {
              // articles are present
              const articles = groups[accountChannel][month][vertical]
              this.addArticlesWithDetails(currentObjects, articles, options.displayAttributes, leftForEachMonth, top, eachColumnWidth)
            }
            top += eachRowHeight
          })
          leftForEachMonth += eachColumnWidth
        })
        if (uniqueVerticalsForAllLaunchMonths.length !== 0) {
          createNewPage = true
          currentVerticals = uniqueVerticalsForAllLaunchMonths.length < 4 ? uniqueVerticalsForAllLaunchMonths.splice(0, uniqueVerticalsForAllLaunchMonths.length) : uniqueVerticalsForAllLaunchMonths.splice(0, 4)
        }
        else {
          currentVerticals = []
        }
      }

      if (currentObjects.length > 0) {
        ttlSlides++
        if (this.isMerch) {
          promises.push(this.createSlide(merch, `${slideNameValue} - ${padStart(currentSlideNbr.toString(), 3, '0')}`, currentObjects.slice(), options, this.imgScaleFactor)
            .catch((error) => {
              // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
              console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
            }))
        }
      }
    }
    return Promise.all(promises)
      .then(() => {
        if (this.isMerch) {
          return ttlSlides
        }
      })
  }

  // on new page will create page header, rest currentTopPosition, reset current row and increase page number
  async onNewPage(catalog: CatalogDetails, currentObjects: any[], accountChannel) {
    currentObjects.length = 0
    currentObjects.push(this.createTitleObj(catalog, accountChannel))
    currentObjects.push(await this.createHeaderLogo(40, this.slideWidth - this.rightPadding - 20))
    currentObjects.push(this.addLine(this.headerProps.height - 10, (this.slideWidth - this.leftPadding) / 2, 0, this.slideWidth - this.leftPadding - this.rightPadding))
  }

  addLaunchMonthsObjects(currentObjects: any[], launchMonths: string[], eachColumnWidth: number, top: number) {
    launchMonths.forEach((month, index) => {
      const leftForEachMonth = this.leftPadding + this.columnProps.labelWidth + (eachColumnWidth * index)
      currentObjects.push(this.createTextObject(month.toUpperCase(), top, leftForEachMonth, eachColumnWidth, 20, this.rowProps.labelFontSize, this.rowProps.fontWeight, 'center', '#848884', false))
    })
  }

  addVerticalLabels(currentObjects: any[], verticals: string[], eachRowHeight: number, top: number) {
    verticals.forEach((vertical, index) => {
      const topForLabel = top + eachRowHeight / 2 + (eachRowHeight * index) - 20
      currentObjects.push(this.createTextObject(vertical, topForLabel, this.leftPadding, this.columnProps.labelWidth, 20, this.columnProps.labelFontSize, this.columnProps.fontWeight, 'center', '#848884', false))
      if (index !== 0 && index < verticals.length) { currentObjects.push(this.addLine(top + (eachRowHeight * index), (this.slideWidth - this.leftPadding) / 2, 0, this.slideWidth - this.leftPadding - this.rightPadding, '#D3D3D3')) }
    })
  }

  addArticlesWithDetails(currentObjects: any[], articles: MyArticle[], displayAttributes: string[], boxLeft: number, boxTop: number, eachColumnWidth: number) {
    const topCount = Math.ceil(articles.length / 2)
    const spacing = (eachColumnWidth - (topCount * this.imgSize)) / topCount
    let top = boxTop + this.imgSize / 2
    let left = boxLeft + this.imgSize / 2
    articles.forEach((article, index) => {
      if (index === topCount) {
        top += this.imgSize + 15
        left = boxLeft + this.imgSize / 2
      }
      currentObjects.push(this.createImage(top, left, article, 'firstImage', this.imgScaleFactor, this.imgScaleFactor))
      currentObjects.push(this.createLabel(top + this.imgSize / 2, left - this.imgSize / 2, this.imgSize + (spacing > 25 ? 25 : spacing), displayAttributes, article, false))
      left += this.imgSize + (spacing > 25 ? 25 : spacing)
    })
  }

  generateGroups(catalog: CatalogDetails, articlesData: MyArticle[], myAttributes: Record<string, IMyAttribute>) {
    const result: Record<string, Record<string, Record<string, MyArticle[]>>> = {}
    articlesData.forEach((article) => {
      // const accountChannels: string[] = utils.isDefined(myAttributes.AccountChannelNew_ON) && utils.isDefined(article.AccountChannelNew_ON) ? article.AccountChannelNew_ON as string[] : ['Blank']
      const accountChannels: string[] = [...new Set(Object.keys(article._Segmentations).filter(seg => article._Segmentations[seg].PlanningValue.toLowerCase() !== 'planned')
        .map(seg => catalog._IndexedCatalogSegmentation[seg] ? `${catalog._IndexedCatalogSegmentation[seg].Name.split('-')[1]?.trim()} | Tier ${catalog._IndexedCatalogSegmentation[seg].Name.split('-')[2]?.trim()} | ${article.GenderName_ON} | Recommended` : '').filter(seg => seg !== ''))]
      const verticalName = utils.isDefined(myAttributes.VerticalName_ON) && utils.isDefined(article.VerticalName_ON) ? article.VerticalName_ON.toString() : ''
      const launchMonth = utils.isDefined(myAttributes.LaunchDate_ON) && article.LaunchDate_ON && validateDate(article.LaunchDate_ON) ? new Date(article.LaunchDate_ON.toString()).toLocaleString('default', { month: 'long' }) : ''
      accountChannels.forEach((accountChannelValue) => {
        if (!result[accountChannelValue]) {
          result[accountChannelValue] = {}
        }
        if (!result[accountChannelValue][launchMonth]) {
          result[accountChannelValue][launchMonth] = {}
        }
        if (!result[accountChannelValue][launchMonth][verticalName.toUpperCase()]) {
          result[accountChannelValue][launchMonth][verticalName.toUpperCase()] = []
        }
        const group = result[accountChannelValue][launchMonth][verticalName.toUpperCase()]
        if (group.length < 10) {
          result[accountChannelValue][launchMonth][verticalName.toUpperCase()].push(article)
        }
      })
    })
    return result
  }

  async createHeaderLogo(top: number, left: number) {
    return {
      type: merchConstants.objectTypes.image,
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 0,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.onLogoData,
    }
  }

  addLine(top: number, left: number, angle: number, width: number, color = '#000000', strokeWidth = 1, templateObject = true, locked = true) {
    return {
      type: 'line',
      top,
      left,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      stroke: color,
      strokeWidth,
      strokeDashArray: null,
      locked,
      width,
      height: 1,
      angle,
      templateObject,
    }
  }

  createTitleObj(catalog: CatalogDetails, accountChannel: string) {
    const title = `${catalog.Season} | ${accountChannel}`
    return {
      type: 'textbox',
      top: 20,
      left: this.leftPadding,
      width: this.slideWidth - 200,
      height: this.titleHeight,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text: title,
      fill: '#000000',
      backgroundColor: '',
      fontFamily: this.font,
      fontSize: 25,
      fontStyle: 'normal',
      fontWeight: 'normal',
      textAlign: 'left',
      locked: true,
      templateObject: false,
      isOriginTopLeft: true,
    }
  }

  createImage(top: number, left: number, article: MyArticle, imageType: string, scaleX: number, scaleY: number) {
    return {
      type: 'articleImage',
      top,
      left,
      width: this.imageWidth,
      height: this.imageHeight,
      articleId: article.Id,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      templateObject: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
      isRequest: article._IsRequestArticle,
    }
  }

  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontWeight: string, textAlign: string, color: string, locked: boolean) {
    text = text != null ? text.toString() : ''
    // text = "•"
    return {
      type: 'textbox',
      top,
      left,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill: color,
      backgroundColor: '',
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked,
      isOriginTopLeft: true,
    }
  }

  createLabel(top: number, left: number, width: number, attributes: string[], article: MyArticle, showLabels = false) {
    if (!attributes || attributes.length === 0) { return }
    return {
      type: 'articleDetails',
      top,
      left,
      width,
      height: article._maxHeight,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill: '#000000',
      backgroundColor: '',
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
      },
      fontFamily: this.font,
      fontSize: this.articleAttributeProp.fontSize,
      fontStyle: this.articleAttributeProp.fontStyle,
      fontWeight: this.articleAttributeProp.fontWeight,
      textAlign: this.articleAttributeProp.textAlign,
      locked: false,
      articleId: article.Id,
      templateObject: false,
      showLabels,
      isOriginTopLeft: true,
      isRequest: article._IsRequestArticle,
    }
  }

  createSlide(merch, slideName, objects: any[], options: Record<string, any>, imageDefaultScaleFactor: number, articleIdList: number[] = []) {
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === slideName))) {
      let c = 0
      do {
        c++
        slideName = `${slideName} (${c})`
      } while (folderNode.children.find(node => node.label === slideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, slideName, objects, this.slideSize, imageDefaultScaleFactor, sortOrder, true, articleIdList)
  }
}

export default new OnTemplate()
